import { Avatar, Button, Dropdown, DropdownProps, Radio, message } from 'antd'
import {
  CreditCardOutlined,
  ExportOutlined,
  LoginOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useParams, useSearchParams } from 'react-router-dom'
import { useLayoutSettingActions, useLayoutSettings } from '@/stores/layoutSettingStore'
import {
  formatQuotaWan,
  isSignedIn,
  isSubActive,
  useSubscriptionStore,
} from '@/stores/subscriptionStore'
import { getThesisPlan } from '@/constants/thesisPlans'
import { exportThesisMarkdown } from '@/lib/exportThesisMarkdown'

type Props = DropdownProps & {
  /** 工作台：账号弹窗内附带导出 / 帮助 */
  workspace?: boolean
}

const UserDropdown = ({ workspace, ...props }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { novelId } = useParams()
  const layoutSettings = useLayoutSettings()
  const { setSettings } = useLayoutSettingActions()
  const profile = useSubscriptionStore((s) => s.profile)
  const sub = useSubscriptionStore((s) => s.sub)
  const signOut = useSubscriptionStore((s) => s.signOut)
  const signedIn = isSignedIn(profile)
  const active = isSubActive(sub)

  const openAuth = () => {
    searchParams.set('modal[auth]', '1')
    setSearchParams(searchParams)
  }

  const openPricing = () => {
    if (!signedIn) {
      openAuth()
      return
    }
    searchParams.set('modal[pricing]', '1')
    setSearchParams(searchParams)
  }

  const openHelp = () => {
    searchParams.set('modal[help]', '1')
    setSearchParams(searchParams)
  }

  const bannerText = signedIn
    ? sub
      ? active
        ? `${getThesisPlan(sub.planId).name} · 剩余约 ${formatQuotaWan(sub.remainingTokens)}`
        : `${getThesisPlan(sub.planId).name} · 已失效`
      : '暂无套餐'
    : '注册即送体验额度'

  const bannerTone = signedIn && active ? 'ok' : 'warn'

  return (
    <Dropdown
      placement="bottomRight"
      trigger={['click']}
      {...props}
      popupRender={() => (
        <div className="thesis-account-panel">
          <div className={`thesis-account-panel__banner is-${bannerTone}`}>
            {bannerText}
          </div>

          <div className="thesis-account-panel__body">
            <div className="thesis-account-panel__name">
              {signedIn ? profile.displayName : '未登录'}
            </div>
            {signedIn && (
              <div className="thesis-account-panel__meta">{profile.email}</div>
            )}

            <div className="thesis-account-panel__theme">
              <span className="thesis-account-panel__label">主题</span>
              <Radio.Group
                value={layoutSettings.themeMode}
                onChange={(e) =>
                  setSettings({
                    ...layoutSettings,
                    themeMode: e.target.value as 'light' | 'dark',
                  })
                }
                size="small"
                optionType="button"
                options={[
                  { label: '浅色', value: 'light' },
                  { label: '深色', value: 'dark' },
                ]}
              />
            </div>
          </div>

          <div className="thesis-account-panel__foot">
            {workspace ? (
              <>
                <button
                  type="button"
                  className="thesis-account-panel__link"
                  onClick={() => exportThesisMarkdown(novelId)}
                >
                  <ExportOutlined />
                  导出 Markdown
                </button>
                <button
                  type="button"
                  className="thesis-account-panel__link"
                  onClick={openHelp}
                >
                  <QuestionCircleOutlined />
                  使用说明
                </button>
              </>
            ) : null}
            {signedIn ? (
              <>
                <button
                  type="button"
                  className="thesis-account-panel__link"
                  onClick={openPricing}
                >
                  <CreditCardOutlined />
                  套餐与额度
                </button>
                <button
                  type="button"
                  className="thesis-account-panel__link is-danger"
                  onClick={() => {
                    signOut()
                    message.info('已退出登录')
                  }}
                >
                  <LogoutOutlined />
                  退出登录
                </button>
              </>
            ) : (
              <Button type="primary" block size="large" icon={<LoginOutlined />} onClick={openAuth}>
                登录 / 注册
              </Button>
            )}
          </div>
        </div>
      )}
    >
      <button type="button" className="p-1" aria-label="账号与设置">
        <Avatar size={32} icon={<UserOutlined />} />
      </button>
    </Dropdown>
  )
}

export default UserDropdown
